import { Plus } from 'lucide-react';
import { type ReactNode, useCallback, useState } from 'react';
import { Link } from 'react-router';
import { t } from '@/lib/i18n';
import { type ListParams, useList } from '@/lib/resources';
import { Button } from './ui/button';
import { Card, PageHeader, Pagination, SearchInput } from './ui/layout';
import { type Column, DataTable } from './ui/table';

/** Standard list screen: search, optional filters, table, pagination, "New" button. */
export function ResourceList<T extends { _id: string }>({
  title,
  subtitle,
  resource,
  basePath,
  columns,
  canCreate = true,
  newLabel = t('New'),
  filters,
  params: fixed,
  initial,
  pageSize = 25,
  empty,
  actions,
  toolbar,
  map,
}: {
  title: string;
  subtitle?: ReactNode;
  resource: string;
  basePath: string;
  columns: Column<T>[];
  canCreate?: boolean;
  newLabel?: string;
  filters?: (set: (key: string, value: string) => void, values: ListParams) => ReactNode;
  params?: ListParams;
  /** Initial values of the filters. */
  initial?: ListParams;
  pageSize?: number;
  empty?: string;
  actions?: ReactNode;
  /** Shown between the page header and the table (e.g. tabs). */
  toolbar?: ReactNode;
  /**
   * Overview map next to the table (beside it on wide screens, above it otherwise). Gets the rows of
   * the current page and the hovered row, and can highlight a row from the map.
   */
  map?: (ctx: {
    rows?: T[];
    /** Current search and filter values, so the map can show the same selection. */
    params: ListParams;
    hovered: string | null;
    hover: (id: string | null) => void;
  }) => ReactNode;
}) {
  const [params, setParams] = useState<ListParams>({ page: 1, pageSize, q: '', ...initial });
  const query = useList<T>(resource, { ...params, ...fixed });
  const setFilter = useCallback(
    (key: string, value: string) => setParams((p) => ({ ...p, [key]: value, page: 1 })),
    [],
  );
  const setQ = useCallback((q: string) => setParams((p) => ({ ...p, q, page: 1 })), []);
  const [hovered, setHovered] = useState<string | null>(null);
  const table = (
    <Card padded={false}>
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 px-4 py-3">
        <SearchInput value={params.q as string} onChange={setQ} />
        {filters?.(setFilter, params)}
      </div>
      <DataTable
        columns={columns}
        rows={query.data?.items}
        loading={query.isFetching}
        rowLink={(r) => `${basePath}/${r._id}`}
        onRowHover={map ? (r) => setHovered(r?._id ?? null) : undefined}
        highlight={hovered}
        empty={empty}
      />
      {query.data && query.data.total > 0 && (
        <Pagination
          page={query.data.page}
          pageSize={query.data.pageSize}
          total={query.data.total}
          onPage={(page) => setParams((p) => ({ ...p, page }))}
        />
      )}
    </Card>
  );
  return (
    <>
      <PageHeader
        title={title}
        subtitle={subtitle}
        actions={
          <>
            {actions}
            {canCreate && (
              <Link to={`${basePath}/new`}>
                <Button icon={<Plus className="size-4" />}>{newLabel}</Button>
              </Link>
            )}
          </>
        }
      />
      {toolbar}
      {map ? (
        <div className="grid gap-4 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
          <div className="min-w-0 xl:order-last">
            <div className="h-80 xl:sticky xl:top-6 xl:h-[calc(100vh-9rem)]">
              {map({ rows: query.data?.items, params, hovered, hover: setHovered })}
            </div>
          </div>
          <div className="min-w-0">{table}</div>
        </div>
      ) : (
        table
      )}
    </>
  );
}
